import React, { useState, useEffect, useRef } from 'react';
import { IspProfileData } from '@/api/entities';
import { ServerCrash } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/components/utils/translations';
import { ProfileDataSection } from '@/components/accounts/ProfileSectionDisplay';
import {
  getIspSectionTitle,
  ISP_PRODUCTS_SECTION_KEY,
  normalizeIspProfileRow,
} from '@/lib/ispProfileRender';

export default function IspProfile({ accountName, accountId, profileData: profileDataProp }) {
  const { t } = useTranslation();
  const [ispData, setIspData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const profileDataPropRef = useRef(profileDataProp);
  profileDataPropRef.current = profileDataProp;

  useEffect(() => {
    if (profileDataProp) {
      const data = profileDataProp?.profile_data ?? profileDataProp;
      setIspData(data);
      setIsLoading(false);
      setError(null);
    }
  }, [profileDataProp]);

  useEffect(() => {
    if (profileDataProp) return;
    if (!accountName && !accountId) {
      setIsLoading(false);
      setError('No account identifier provided.');
      return;
    }

    const fetchProfile = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let results = [];

        if (accountId) {
          results = await IspProfileData.filter({ account_id: accountId });
        }

        if (results.length === 0 && accountName) {
          results = await IspProfileData.filter({ account_name: accountName });
        }

        if (profileDataPropRef.current) return;

        if (results?.length > 0) {
          setIspData(results[0].profile_data);
        } else {
          setIspData(null);
        }
      } catch (err) {
        console.error('Failed to fetch ISP profile:', err);
        setError('Could not load products.');
      } finally {
        if (!profileDataPropRef.current) setIsLoading(false);
      }
    };

    fetchProfile();
  }, [accountName, accountId, profileDataProp]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10 text-red-500">
        <ServerCrash className="w-8 h-8 mx-auto mb-2" />
        <p>{error}</p>
      </div>
    );
  }

  const productsSection = ispData?.[ISP_PRODUCTS_SECTION_KEY];
  const rows = Array.isArray(productsSection)
    ? productsSection.map(normalizeIspProfileRow)
    : [];

  if (!rows.length) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">Products not available for this account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ProfileDataSection
        title={getIspSectionTitle(ISP_PRODUCTS_SECTION_KEY)}
        data={rows}
        t={t}
      />
    </div>
  );
}
